import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Download, Expand } from "lucide-react";
import { Skeleton } from "@mui/material";
import "../styles/MedienDetails.css";
import {
  ButtonContainer,
  ContentContainer,
  FactText,
  FullscreenIconContainer,
  OverlayImage,
  StyledButton,
  TitleElement,
} from "../components/StyledComponents";

const MedienDetails = ({ medienData, configSettings, onClose }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState(
    medienData?.thumbnailUrl || null
  );

  useEffect(() => {
    const setOverlayHeight = () => {
      const overlay = document.querySelector(".media-overlay");
      const widget = document.querySelector(".widget-content");

      if (overlay && widget) {
        const maxHeight = widget.getBoundingClientRect().height;
        overlay.style.height = `${maxHeight}px`;
        overlay.style.top = `${widget.getBoundingClientRect().top}px`;
      }
    };

    setOverlayHeight();
    window.addEventListener("resize", setOverlayHeight);

    return () => window.removeEventListener("resize", setOverlayHeight);
  }, []);

  if (!medienData) {
    return (
      <div className="mediao-verlay">
        <p>Medien nicht gefunden oder ungültige Daten.</p>
      </div>
    );
  }

  const currentImage = medienData.thumbnailUrl;

  const openOverlay = (imageUrl) => {
    setSelectedImageUrl(imageUrl);
    setImageLoaded(false);
    setIsOverlayOpen(true);
  };

  const closeOverlay = () => {
    setIsOverlayOpen(false);
    setSelectedImageUrl(null);
  };

  return (
    <div className="media-overlay">
      <div
        className="media-detail-container"
        onClick={(e) => e.stopPropagation()}
      >
        <ContentContainer>
          <TitleElement styling={configSettings?.theme}>
            {medienData.title || "Kein Titel verfügbar"}
          </TitleElement>

          {currentImage && (
            <>
              {!imageLoaded && (
                <Skeleton variant="rectangular" width="100%" height={300} />
              )}
              <OverlayImage
                src={currentImage}
                alt={medienData.title || "Medienbild"}
                onLoad={() => setImageLoaded(true)}
                style={{ display: imageLoaded ? "block" : "none" }}
              />
              <FullscreenIconContainer
                onClick={() => openOverlay(currentImage)}
              >
                <Expand size={32} />
              </FullscreenIconContainer>
            </>
          )}

          {medienData.description && (
            <FactText
              className="mediainfo-texts"
              styling={configSettings?.theme}
            >
              <strong>Beschreibung:</strong> {medienData.description}
            </FactText>
          )}

          {medienData.type && (
            <FactText
              className="mediainfo-texts"
              styling={configSettings?.theme}
            >
              <strong>Typ:</strong> {medienData.type}
            </FactText>
          )}

          {medienData.createdAt && (
            <FactText
              className="mediainfo-texts"
              styling={configSettings?.theme}
            >
              <strong>Hochgeladen am:</strong>{" "}
              {new Date(medienData.createdAt).toLocaleDateString("de-DE")}
            </FactText>
          )}
        </ContentContainer>

        {isOverlayOpen && (
          <div className="media-overlay" onClick={closeOverlay}>
            <div
              className="media-detail-container"
              onClick={(e) => e.stopPropagation()}
            >
              {!imageLoaded && (
                <Skeleton variant="rectangular" width="100%" height={400} />
              )}
              <OverlayImage
                src={selectedImageUrl}
                alt="Selected Media"
                style={{ display: imageLoaded ? "block" : "none" }}
                onLoad={() => setImageLoaded(true)}
              />
              <ButtonContainer>
                <StyledButton
                  styling={configSettings?.theme}
                  variant="contained"
                  color="secondary"
                  onClick={closeOverlay}
                >
                  Zurück
                </StyledButton>
                {selectedImageUrl && (
                  <a
                    href={selectedImageUrl}
                    download
                    style={{ textDecoration: "none" }}
                  >
                    <StyledButton
                      styling={configSettings?.theme}
                      variant="contained"
                      color="secondary"
                      endIcon={<Download size={18} />}
                    >
                      Download
                    </StyledButton>
                  </a>
                )}
              </ButtonContainer>
            </div>
          </div>
        )}

        <ButtonContainer>
          <StyledButton
            styling={configSettings?.theme}
            variant="contained"
            color="primary"
            onClick={onClose}
          >
            Zurück
          </StyledButton>
          {currentImage && (
            <a href={currentImage} download style={{ textDecoration: "none" }}>
              <StyledButton
                styling={configSettings?.theme}
                variant="contained"
                color="primary"
                endIcon={<Download size={18} />}
              >
                Download
              </StyledButton>
            </a>
          )}
        </ButtonContainer>
      </div>
    </div>
  );
};

MedienDetails.propTypes = {
  medienData: PropTypes.shape({
    id: PropTypes.number,
    title: PropTypes.string,
    thumbnailUrl: PropTypes.string,
    url: PropTypes.string,
    description: PropTypes.string,
    type: PropTypes.string,
    createdAt: PropTypes.string,
  }).isRequired,
  configSettings: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default MedienDetails;
